import React, { useState, useEffect } from 'react';
import {
  Search,
  Trash2,
  Flag,
  Eye,
  MessageSquare,
  Heart,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  AlertTriangle
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminPostsApi } from '../../services/adminApi';

const AdminModerationPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reportedOnly, setReportedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedPost, setSelectedPost] = useState(null);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await adminPostsApi.getAll({
        search,
        reported: reportedOnly || undefined,
        page,
        limit: 20
      });
      setPosts(data.posts);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [search, reportedOnly, page]);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu paylaşımı silmek istediğinizden emin misiniz?')) return;
    try {
      await adminPostsApi.delete(id);
      fetchPosts();
      if (selectedPost?.id === id) setSelectedPost(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Moderasyon</h1>
        <p className="text-slate-400 mt-1">Topluluk paylaşımlarını yönetin ({total} paylaşım)</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="Paylaşım ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <Button
          variant={reportedOnly ? "default" : "outline"}
          onClick={() => setReportedOnly(!reportedOnly)}
          className={reportedOnly ? "bg-red-500 hover:bg-red-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}
        >
          <Flag className="w-4 h-4 mr-2" />
          Şikayet Edilenler
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Posts List */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-slate-400 bg-slate-800/50 border border-slate-700 rounded-xl">
              Paylaşım bulunamadı
            </div>
          ) : (
            posts.map((post) => (
              <div
                key={post.id}
                className={`bg-slate-800/50 border rounded-xl p-4 cursor-pointer transition-all ${selectedPost?.id === post.id ? 'border-emerald-500' : 'border-slate-700 hover:border-slate-600'}`}
                onClick={() => setSelectedPost(post)}
              >
                <div className="flex items-start gap-4">
                  {post.image && (
                    <img src={post.image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{post.authorName || 'Anonim'}</span>
                      {post.reported && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">
                          <AlertTriangle className="w-3 h-3" /> Şikayet
                        </span>
                      )}
                    </div>
                    <p className="text-slate-300 text-sm mt-1 line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" /> {post.likes || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" /> {post.comments || 0}
                      </span>
                      <span>{post.timestamp || ''}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-slate-400">Sayfa {page} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedPost ? (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Paylaşım Detayı</h3>
                <button onClick={() => setSelectedPost(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {selectedPost.image && (
                <img src={selectedPost.image} alt="" className="w-full h-48 rounded-xl object-cover mb-4" />
              )}

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-400">Yazar</p>
                  <p className="text-white">{selectedPost.authorName || 'Anonim'}</p>
                </div>

                <div>
                  <p className="text-sm text-slate-400">İçerik</p>
                  <p className="text-slate-300">{selectedPost.content}</p>
                </div>

                {selectedPost.placeName && (
                  <div>
                    <p className="text-sm text-slate-400">Mekan</p>
                    <p className="text-white">{selectedPost.placeName}</p>
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Beğeni</p>
                    <p className="text-white font-medium">{selectedPost.likes || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Yorum</p>
                    <p className="text-white font-medium">{selectedPost.comments || 0}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Ziyaret</p>
                    <p className="text-white font-medium">{selectedPost.visited || 0}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <Button
                    onClick={() => handleDelete(selectedPost.id)}
                    className="w-full bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Paylaşımı Sil
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
              <Eye className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Detayları görmek için bir paylaşım seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminModerationPage;
